import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FicScreenContentBase } from '@fi-shared/fic-screen/fic-screen-content-base';
import { IChatConversation, INewConversationData, IOpenConversationManageUsersData } from '@fi-shared/fi-chat/fi-chat';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import {
    FiChatConversationManageUsersScreenComponent,
    FiChatConversationManageUsersScreenData,
} from '@fi-shared/fi-chat/fi-chat-conversation/fi-chat-conversation-manage-users-screen/fi-chat-conversation-manage-users-screen.component';
import { FicScreenService } from '@fi-shared/fic-screen/fic-screen.service';
import { isIOS } from '@fi-shared/utils/isIOS';
import { ManageUsersStep } from '@fi-shared/fi-chat/fi-chat-conversation-manage-type';
import { isDefined } from '@fi-shared/utils';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';

export interface FiChatConversationScreenData {
    conversation$: Subject<IChatConversation>;
    onStartNewConversation: (newConversationData: INewConversationData) => void;
    managingUsersMode: boolean;
    manageUsersStep: ManageUsersStep;
}

@Component({
    selector: 'fi-chat-conversation-screen',
    template: `
        @if (data.conversation$ | async) {
          <fic-screen>
            <fic-screen-header>
              <fic-screen-header-prefix>
                <fic-screen-btn-close></fic-screen-btn-close>
              </fic-screen-header-prefix>
              <span class="conversation-name">{{ conversation.Name }}</span>
              <fic-screen-header-suffix>
                <fi-chat-conversation-settings-menu
                  [conversation]="conversation"
                  [isNewConversation]="isNewConversation()"
                  [managingUsersMode]="managingUsersMode"
                  (managingUsersModeChange)="setManagingUsersMode($event)"
                  (onCloseConversation)="onCloseConversation()"
                  (onStartNewConversation)="onStartNewConversation($event)"
                  >
                </fi-chat-conversation-settings-menu>
              </fic-screen-header-suffix>
            </fic-screen-header>
            <fic-screen-content>
              <fi-chat-conversation
                [conversation]="conversation"
                [isNewConversation]="isNewConversation()"
                (startNewConversation)="data.onStartNewConversation($event)"
                (closeConversation)="onCloseConversation()"
                >
              </fi-chat-conversation>
            </fic-screen-content>
          </fic-screen>
        }
        `,
    styleUrls: ['./fi-chat-conversation-screen.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-screen' },
    standalone: false,
})
export class FiChatConversationScreenComponent
    extends FicScreenContentBase<FiChatConversationScreenData>
    implements OnInit, OnDestroy
{
    fiChatHelperService = this.injector.get(FiChatHelperService);
    fiChatSyncService = this.injector.get(FiChatSyncService);
    ficScreenService = this.injector.get(FicScreenService);
    managingUsersMode = false;
    @HostBinding('class.ios') isIOS = isIOS();
    protected conversation: IChatConversation;

    ngOnInit(): void {
        const cdr = this.injector.get(ChangeDetectorRef);

        this.fiChatSyncService
            .conversationUpdated$(this.conversation?.Id)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                cdr.markForCheck();
            });

        this.data.conversation$.subscribe((conversation) => {
            this.conversation = conversation;
        });

        if (this.data.managingUsersMode) {
            this.managingUsersMode = true;
            this.checkOpenConversationManageUsersScreen();
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    onStartNewConversation($event: INewConversationData) {
        this.closeScreen(); // https://chainformation.atlassian.net/browse/FI-4194
        this.data.onStartNewConversation($event);
    }

    onCloseConversation() {
        this.closeScreen();
    }

    isNewConversation() {
        return this.fiChatHelperService.isConversationNew(this.conversation);
    }

    setManagingUsersMode(manageUsersData: IOpenConversationManageUsersData) {
        if (manageUsersData.status) this.checkOpenConversationManageUsersScreen(manageUsersData.currentStep);
    }

    private checkOpenConversationManageUsersScreen(currentStep?: ManageUsersStep) {
        const conversationData: FiChatConversationManageUsersScreenData = {
            conversation$: new ReplaySubject<IChatConversation>(1),
            manageUsersStep: isDefined(currentStep) ? currentStep : this.data.manageUsersStep,
        };
        conversationData.conversation$.next(this.conversation);
        this.ficScreenService.openScreen({
            componentType: FiChatConversationManageUsersScreenComponent,
            data: conversationData,
        });
    }
}
